// Create this new component for handling product variations

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip
} from '@mui/material';
import { Add, LocalOffer } from '@mui/icons-material';

const ProductCard = ({ product, onAddToCart, settings }) => {
  const [variationDialog, setVariationDialog] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);

  const hasVariations = product.variations && product.variations.length > 0;
  
  // SAFE: Ensure price is always a valid number
  const basePrice = parseFloat(product.price) || 0;

  
  const handleAddToCart = () => {
    if (hasVariations) {
      setVariationDialog(true);
    } else {
      onAddToCart(product);
    }
  };

  const handleVariationSelect = () => {
    // SAFE: Ensure both prices are valid numbers
    const variationPrice = parseFloat(selectedVariation?.price) || 0;
    const finalPrice = basePrice + variationPrice;
    
    const finalProduct = {
      ...product,
      selectedVariation,
      finalPrice: finalPrice, // Use calculated finalPrice instead of product.price + variation
      displayName: selectedVariation 
        ? `${product.name} (${selectedVariation.name})`
        : product.name
    };
    
    onAddToCart(finalProduct);
    setVariationDialog(false);
    setSelectedVariation(null);
  };

   const selectedVariationPrice = parseFloat(selectedVariation?.price) || 0;
    const finalPrice = basePrice + selectedVariationPrice;

  return (
    <>
      <Card 
        className="product-card"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          borderRadius: 2,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 3
          }
        }}
        onClick={handleAddToCart}
      >
        {product.image && (
          <CardMedia
            component="img"
            height="120"
            image={product.image}
            alt={product.name}
            sx={{ objectFit: 'cover' }}
          />
        )}
        
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            fontSize: '0.95rem',
            mb: 1,
            lineHeight: 1.3
          }}>
            {product.name}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
              {settings?.defaultSettings?.currencySymbol || '$'}{basePrice.toFixed(2)}
              {hasVariations && (
                <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
                  +
                </Typography>
              )}
            </Typography>
            
            {hasVariations && (
              <Chip 
                icon={<LocalOffer />}
                label={`${product.variations.length} options`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          
          {product.description && (
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: '0.8rem',
              lineHeight: 1.4,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {product.description}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Variations Dialog */}
      <Dialog 
        open={variationDialog} 
        onClose={() => setVariationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6">{product.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose your option
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 2 }}>Available Options:</FormLabel>
            <RadioGroup
              value={selectedVariation?.variation_id || ''}
              onChange={(e) => {
                const variation = product.variations.find(v => v.variation_id === parseInt(e.target.value));
                setSelectedVariation(variation);
              }}
            >
              {/* Base option */}
              <FormControlLabel
                value=""
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                    <Typography>Regular</Typography>
                    <Typography color="primary" fontWeight={600}>
                      {settings?.defaultSettings?.currencySymbol || '$'}{basePrice.toFixed(2)}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
              
              {/* Variations */}
              {product.variations.map((variation) => {
                // SAFE: Ensure variation price is a valid number
                const variationPrice = parseFloat(variation.price) || 0;
                const combinedPrice = basePrice + variationPrice;
                
                return (
                  <FormControlLabel
                    key={variation.variation_id}
                    value={variation.variation_id}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                        <Typography>{variation.name}</Typography>
                        <Typography color="primary" fontWeight={600}>
                          {settings?.defaultSettings?.currencySymbol || '$'}{combinedPrice.toFixed(settings?.defaultSettings?.decimalPlaces || 2)}
                          {variationPrice > 0 && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'success.main' }}>
                              (+{settings?.defaultSettings?.currencySymbol || '$'}{variationPrice.toFixed(settings?.defaultSettings?.decimalPlaces || 2)})
                            </Typography>
                          )}
                          </Typography>
                        </Box>
                      }
                      sx={{ mb: 1 }}
                    />
                  );
                })}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setVariationDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleVariationSelect}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
              }
            }}
          >
            Add to Cart - {settings?.defaultSettings?.currencySymbol || '$'}{finalPrice.toFixed(2)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductCard;